/**
 * ChatWidget Component
 *
 * Floating chat panel.
 * Collapsed: circular button with chat icon.
 * Expanded: panel with conversation list, message history, text input,
 * voice recorder, and send button.
 *
 * On mobile (viewport width <= 480px), the expanded panel takes up the
 * full screen. On desktop, it opens as a right-side full-height sidebar
 * (Apollo-style) for a larger workspace.
 *
 * Uses the useChat hook for all state and API interactions.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Input, Tooltip, Spin, Empty, Dropdown } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import {
  MessageOutlined,
  SendOutlined,
  MinusOutlined,
  PlusOutlined,
  DeleteOutlined,
  HistoryOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useChat } from '@/hooks/useChat';
import type { PageContext } from '@/services/chat.service';
import MessageBubble from './MessageBubble';
import VoiceRecorder from './VoiceRecorder';

const { TextArea } = Input;

function parsePageContext(pathname: string): PageContext | undefined {
  const path = pathname || '';
  let match: RegExpMatchArray | null = null;
  if ((match = path.match(/\/opportunities\/([0-9a-f-]{36})/i)) && !path.includes('/create')) {
    return { path, entityType: 'opportunity', entityId: match[1] };
  }
  if ((match = path.match(/\/all-opportunities\/([0-9a-f-]{36})/i))) {
    return { path, entityType: 'opportunity', entityId: match[1] };
  }
  if ((match = path.match(/\/my-opportunities\/([0-9a-f-]{36})/i))) {
    return { path, entityType: 'opportunity', entityId: match[1] };
  }
  if ((match = path.match(/\/quotes\/([0-9a-f-]{36})/i)) && !path.includes('/create')) {
    return { path, entityType: 'quote', entityId: match[1] };
  }
  if ((match = path.match(/\/all-quotes\/([0-9a-f-]{36})/i))) {
    return { path, entityType: 'quote', entityId: match[1] };
  }
  if ((match = path.match(/\/my-quotes\/([0-9a-f-]{36})/i))) {
    return { path, entityType: 'quote', entityId: match[1] };
  }
  if ((match = path.match(/\/proposals\/([0-9a-f-]{36})/i))) {
    return { path, entityType: 'proposal', entityId: match[1] };
  }
  if ((match = path.match(/\/customer-accounts\/([0-9a-f-]{36})/i)) && !path.includes('/create')) {
    return { path, entityType: 'customerAccount', entityId: match[1] };
  }
  if ((match = path.match(/\/all-customer-accounts\/([0-9a-f-]{36})/i))) {
    return { path, entityType: 'customerAccount', entityId: match[1] };
  }
  return undefined;
}

const DESKTOP_SIDEBAR_WIDTH = 460;
const MOBILE_BREAKPOINT = 480;

const ChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<TextAreaRef | null>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const {
    conversations,
    messages,
    streamingMessage,
    isStreaming,
    isTranscribing,
    isLoading,
    sendMessage,
    stopGeneration,
    transcribeAudio,
    createConversation,
    switchConversation,
    deleteConversation,
    loadConversations,
  } = useChat();

  const location = useLocation();
  const pageContext = useMemo(() => parsePageContext(location.pathname), [location.pathname]);

  useEffect(() => {
    if (isOpen) {
      loadConversations();
    }
  }, [isOpen, loadConversations]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMessage]);

  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || isStreaming) return;
    setInputValue('');
    await sendMessage(text, pageContext);
  }, [inputValue, isStreaming, sendMessage, pageContext]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handleTranscription = useCallback((text: string) => {
    setInputValue(prev => prev ? `${prev} ${text}` : text);
    inputRef.current?.focus();
  }, []);

  const handleNewChat = useCallback(async () => {
    await createConversation();
  }, [createConversation]);

  const conversationMenuItems = conversations.map(c => ({
    key: c.id,
    label: (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 200 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {c.title || 'New conversation'}
        </span>
        <Button
          type="text"
          size="small"
          icon={<DeleteOutlined />}
          onClick={(e) => { e.stopPropagation(); deleteConversation(c.id); }}
          style={{ marginLeft: 8, color: '#8c8c8c' }}
        />
      </div>
    ),
    onClick: () => switchConversation(c.id),
  }));

  if (!isOpen) {
    return (
      <div className="portal-chat" style={{ position: 'fixed', bottom: isMobile ? 16 : 24, right: isMobile ? 16 : 24, zIndex: 1000 }}>
        <Tooltip title="AI Assistant" placement="left">
          <Button
            type="primary"
            shape="circle"
            size="large"
            icon={<MessageOutlined />}
            onClick={() => setIsOpen(true)}
            style={{
              width: 56,
              height: 56,
              fontSize: 24,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }}
          />
        </Tooltip>
      </div>
    );
  }

  const panelStyle: React.CSSProperties = isMobile
    ? {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        borderRadius: 0,
        boxShadow: 'none',
        border: 'none',
        overflow: 'hidden',
      }
    : {
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: DESKTOP_SIDEBAR_WIDTH,
        height: '100vh',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        borderRadius: 0,
        boxShadow: '-8px 0 24px rgba(0, 0, 0, 0.12)',
        borderLeft: '1px solid #e8e8e8',
        overflow: 'hidden',
      };

  return (
    <div className="portal-chat" style={panelStyle}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isMobile ? '14px 16px' : '12px 16px',
          borderBottom: '1px solid #f0f0f0',
          background: '#fafafa',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>AI Assistant</span>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <Tooltip title="New chat">
            <Button type="text" size="small" icon={<PlusOutlined />} onClick={handleNewChat} />
          </Tooltip>
          {conversations.length > 0 && (
            <Dropdown menu={{ items: conversationMenuItems }} trigger={['click']} placement="bottomRight">
              <Tooltip title="Chat history">
                <Button type="text" size="small" icon={<HistoryOutlined />} />
              </Tooltip>
            </Dropdown>
          )}
          <Tooltip title={isMobile ? 'Close' : 'Minimize'}>
            <Button type="text" size="small" icon={<MinusOutlined />} onClick={() => setIsOpen(false)} />
          </Tooltip>
        </div>
      </div>

      {/* Messages */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 12px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : messages.length === 0 && !streamingMessage ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 8 }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span style={{ color: '#8c8c8c', fontSize: 13 }}>
                  Ask me to search products, create quotes, or look up any portal data.
                </span>
              }
            />
          </div>
        ) : (
          <>
            {messages
              .filter((msg) => msg.role === 'user' || msg.role === 'assistant')
              .map((msg) => (
                <MessageBubble
                  key={msg.id}
                  role={msg.role}
                  content={msg.content}
                />
              ))}
            {streamingMessage && streamingMessage.content && (
              <MessageBubble
                role="assistant"
                content={streamingMessage.content}
                isStreaming={streamingMessage.isStreaming}
              />
            )}
            {isStreaming && (!streamingMessage || !streamingMessage.content) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  marginBottom: 12,
                }}
              >
                <LoadingOutlined style={{ color: '#1677ff', fontSize: 14 }} spin />
                <span style={{ fontSize: 13, color: '#8c8c8c' }}>
                  Working on it...
                </span>
              </div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div
        style={{
          padding: isMobile ? '8px 12px 16px' : '8px 12px 12px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          gap: 8,
          alignItems: 'flex-end',
          flexShrink: 0,
        }}
      >
        <VoiceRecorder
          onTranscription={handleTranscription}
          transcribeAudio={transcribeAudio}
          isTranscribing={isTranscribing}
          disabled={isStreaming}
        />
        <TextArea
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask anything..."
          autoSize={{ minRows: 1, maxRows: 4 }}
          style={{ flex: 1, borderRadius: 8, resize: 'none' }}
          disabled={isStreaming}
        />
        {isStreaming ? (
          <Tooltip title="Stop">
            <Button
              danger
              icon={
                <span style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  background: '#ff4d4f',
                  borderRadius: 2,
                }} />
              }
              onClick={stopGeneration}
              style={{ borderRadius: 8 }}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Send (Enter)">
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              disabled={!inputValue.trim()}
              style={{ borderRadius: 8 }}
            />
          </Tooltip>
        )}
      </div>

      {/* CSS animations and chat copy-button hover behaviour */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        .chat-message-row:hover .chat-copy-wrapper {
          opacity: 1 !important;
        }
        @media (max-width: ${MOBILE_BREAKPOINT}px) {
          .chat-copy-wrapper {
            opacity: 1 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ChatWidget;
